// src/ui/styles/slideshow.css.js
//
// Soft Midnight Glass — Slideshow Styles (Final Fix)
// --------------------------------------------------
// Key fixes:
//   - Removed absolute positioning from <img>
//   - Ensured .dsl-cell has real dimensions
//   - Added min-width/min-height everywhere needed
//   - Ensured images always scale to fit the grid cell
//   - Preserved crossfade layers without breaking layout

import { css } from "lit";

export const slideshowStyles = css`
  :host {
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;

    background: #0f0f12;
    color: #fff;
  }

  /* ------------------------------------------------------------
     Slideshow outer container
  ------------------------------------------------------------ */
  .slideshow {
    position: relative;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes zoomIn {
    from {
      transform: scale(0.6);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  /* ------------------------------------------------------------
     Crossfade layers
  ------------------------------------------------------------ */
  .img-layer {
    position: absolute;
    inset: 0;

    display: flex;
    width: 100%;
    height: 100%;

    opacity: 0;
    transition-duration: var(--dur, 0.7s);
    transition-timing-function: ease;
    will-change:
      opacity, transform, filter, clip-path, mask-image, backdrop-filter;

    /* allow shrinking */
    min-width: 0;
    min-height: 0;

    pointer-events: none;
  }

  .img-layer.active {
    opacity: 1;
    pointer-events: auto;
  }

  /* ------------------------------------------------------------
  Soft Glass Dissolve
  ------------------------------------------------------------ */
  .slideshow.transition-soft-glass .img-layer {
    filter: blur(20px);
    transform: scale(1.05);
    transition-property: opacity, filter, transform;
  }

  .slideshow.transition-soft-glass .img-layer.active {
    filter: blur(0px);
    transform: scale(1);
  }
  /* ------------------------------------------------------------
  Slide
  ------------------------------------------------------------ */

  .slideshow.transition-slide .img-layer {
    transform: translateX(20px) scale(1.02);
    transition-property: opacity, transform;
  }

  .slideshow.transition-slide .img-layer.active {
    transform: translateX(0) scale(1);
  }

  /* ------------------------------------------------------------
  Radial Reveal
  ------------------------------------------------------------ */

  .slideshow.transition-radial .img-layer {
    clip-path: circle(0% at 50% 50%);
    transition-property: opacity, clip-path;
  }

  .slideshow.transition-radial .img-layer.active {
    clip-path: circle(150% at 50% 50%);
  }

  /* ------------------------------------------------------------
  Wipe
  ------------------------------------------------------------ */
  .slideshow.transition-wipe .img-layer {
    mask-image: linear-gradient(to right, transparent 0%, black 0%);
    transition-property: opacity, mask-image;
  }

  .slideshow.transition-wipe .img-layer.active {
    mask-image: linear-gradient(to right, transparent 0%, black 100%);
  }

  /* ------------------------------------------------------------
  Noise Desolve 
  ------------------------------------------------------------ */
  .slideshow.transition-noise .img-layer {
    mask-image: paint(noise);
    mask-size: 200% 200%;
    opacity: 0;
    animation: noiseReveal var(--dur) forwards;
  }

  .slideshow.transition-noise .img-layer.active {
    opacity: 1;
  }

  @keyframes noiseReveal {
    from {
      mask-position: 0% 0%;
    }
    to {
      mask-position: 100% 100%;
    }
  }

  /* ------------------------------------------------------------
Film Burn
  ------------------------------------------------------------ */

  .slideshow.transition-film-burn .img-layer {
    opacity: 0;
    filter: brightness(1.8) saturate(1.4) blur(10px);
    transform: scale(1.1);
    transition-property: opacity, filter, transform;
  }

  .slideshow.transition-film-burn .img-layer.active {
    filter: brightness(1) saturate(1) blur(0px);
    transform: scale(1);
  }

  /* ------------------------------------------------------------
     DSL grid container
  ------------------------------------------------------------ */
  .dsl-grid {
    display: grid;
    width: 100%;
    height: 100%;

    /* CRITICAL: allow grid to shrink */
    min-width: 0;
    min-height: 0;
  }

  /* ------------------------------------------------------------
     DSL grid cell
  ------------------------------------------------------------ */
  .dsl-cell {
    position: relative;
    overflow: hidden;
    border-radius: var(--glass-radius-lg);

    /* CRITICAL: enforce real size */
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ------------------------------------------------------------
     Image rendering (independent fit)
  ------------------------------------------------------------ */
  .dsl-cell video,
  .dsl-cell img {
    /* NORMAL FLOW — not absolute */
    width: 100%;
    height: 100%;

    /* object-fit: contain; */
    object-position: center;

    /* allow shrinking */
    min-width: 0;
    min-height: 0;

    /* prevent native image drag from swallowing click/pan gestures */
    user-select: none;
    -webkit-user-drag: none;

    transition:
      opacity var(--transition-slow),
      transform var(--transition-slow);
  }

  .fit {
    object-fit: cover;
  }

  .fill {
    object-fit: contain;
  }

  /* ------------------------------------------------------------
     Smooth layout transitions
  ------------------------------------------------------------ */
  .layout-transition {
    transition:
      opacity var(--transition-slow),
      transform var(--transition-slow);
  }

  /* ------------------------------------------------------------
     Help overlay when empty
  ------------------------------------------------------------ */
  .empty-help {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  @keyframes helpZoomIn {
    from {
      opacity: 0;
      transform: scale(0.92) translateY(8px);
      filter: blur(4px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
      filter: blur(0);
    }
  }
  .empty-help-box {
    text-align: center;
    padding: 16px 20px;
    border-radius: 16px;
    max-width: 480px;
    opacity: 0;
    transform: scale(0.92);
    animation: helpZoomIn 320ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  .empty-help-box h2 {
    margin: 16px 0 8px;
    font-size: 1.6rem;
  }

  .empty-help-box p {
    opacity: 0.8;
    margin-bottom: 16px;
  }

  .empty-help-box ul {
    list-style: none;
    padding: 0;
    margin: 0 0 24px;
    opacity: 0.75;
    font-size: 0.9rem;
  }

  .empty-help-box ul li {
    margin: 6px 0;
  }

  .shortcut-hints {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    margin-bottom: 24px;
    opacity: 0.7;
    font-size: 0.8rem;
  }

  .shortcut-hints strong {
    background: rgba(255, 255, 255, 0.08);
    padding: 2px 6px;
    border-radius: 6px;
  }

  /* ------------------------------------------------------------
     Zoom Styles
  ------------------------------------------------------------ */

  .zoom-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    z-index: 9999;
  }

  .zoom-container {
    position: absolute;
    inset: 0;
    overflow: hidden;
    cursor: grab;
  }

  .zoom-container:active {
    cursor: grabbing;
  }

  .zoom-image {
    position: absolute;
    top: 0;
    left: 0;
    max-width: none;
    max-height: none;
    transform-origin: top left;
    user-select: none;
    -webkit-user-drag: none;
  }

  img .zoom-image {
    object-fit: scale-down;
  }

  video .zoom-image {
    object-fit: scale-down;
  }

  /* Hide keyboard hints on touch devices */
  @media (pointer: coarse) {
    .shortcut-hints {
      display: none;
    }
  }
`;
